import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PageContentCart,
  Sheet,
  SheetContentCheckout,
  SheetContentPickupOrderStatus,
  SheetContentDeliveryOrderStatus,
  SheetContentUpdateProduct,
  SheetContentOffer,
  Alert,
  AlertAddCardClover,
  MapOrderTracking,
  OptionRow,
  getLocationForOrder,
  useOrderStore,
  useCartStore,
  useRewardsStore,
  useRestaurantStore,
  useUIStore,
  useAuthStore,
  hasCompleteName,
  validateOffer,
  OfferWarningType,
} from '@restaurant/core'
import type { Order } from '@restaurant/core'
import type { PlaceOrderResult } from '@restaurant/viewmodels'
import { useCartPageViewModel, useCheckoutViewModel } from '../viewmodels/pages/WebCartPageViewModel'
import { useEditCartItem, WebLocationService } from '@restaurant/viewmodels'
import { useOrderTrackingViewModel } from '../viewmodels/sheets/WebOrderTrackingViewModel'
import { useCloverCardAdd } from '../hooks/useCloverCardAdd'
import { useOfferAlerts } from '../hooks/useOfferAlerts'

export default function CartPage() {
  const navigate = useNavigate()
  const orderTrackingViewModel = useOrderTrackingViewModel()

  // View-mode state: 'checkout' while filling in payment details,
  // 'order-tracking' after a successful place_order response.
  const [viewMode, setViewMode] = useState<'checkout' | 'order-tracking'>('checkout')
  const [completedOrder, setCompletedOrder] = useState<Order | null>(null)

  // Callback passed to CheckoutViewModel so it can signal a successful order.
  const onOrderPlaced = (result: PlaceOrderResult) => {
    const order = result.order as Order
    orderTrackingViewModel.loadOrderTracking(order)
    setCompletedOrder(order)
    setViewMode('order-tracking')
    // Navigate back to menu — the active order toast in the nav bar
    // lets the user reopen tracking from any page.
    navigate('/menu')
  }

  // Add Card modal state
  const [isAddCardOpen, setIsAddCardOpen] = useState(false)
  const merchantId = useRestaurantStore((s) => s.currentLocation?.merchantId) ?? ''

  const cartViewModel = useCartPageViewModel()
  const checkoutViewModel = useCheckoutViewModel({
    onOrderPlaced,
    onAddCardPress: useCallback(() => {
      // Clover customer creation hard-requires a first AND last name. Users
      // whose profile is missing either can't add a card, so route them to
      // complete their profile before opening the payment iframe.
      const user = useAuthStore.getState().user
      if (!hasCompleteName(user)) {
        useUIStore.getState().showToast('Add your first and last name to your account to save a card')
        navigate('/account/manage')
        return
      }
      setIsAddCardOpen(true)
    }, [navigate]),
    onChangeLocation: useCallback(() => {
      setIsCheckoutSheetOpen(false)
      useUIStore.getState().requestLocationSheet()
    }, []),
  })

  const editItem = useEditCartItem({ onSave: () => cartViewModel.refresh() })

  const editOptionGroups = useMemo(() =>
    editItem.optionGroups.map((group) => ({
      ...group,
      children: (
        <>
          {group.options.map((opt) => (
            <OptionRow
              key={opt.id}
              text={`${opt.name}${opt.price > 0 ? ` (+$${opt.price.toFixed(2)})` : ''}`}
              optionType={opt.isRadio ? 'radio' : 'checkbox'}
              value={opt.id}
              name={group.id}
              selected={opt.selected}
              onChange={(value: string, selected: boolean) =>
                editItem.onOptionChange(group.id, value, selected, opt.isRadio)
              }
            />
          ))}
        </>
      ),
    })),
  [editItem.optionGroups, editItem.onOptionChange])

  const { orderType } = useOrderStore()
  const { hasItemsRequiringReview } = useCartStore()
  const currentRestaurant = useRestaurantStore((s) => s.currentRestaurant)
  const currentLocation = useRestaurantStore((s) => s.currentLocation)
  const activeOrderDriverLocation = useOrderStore((s) => s.activeOrderDriverLocation)
  const activeOrderTrackingStatus = useOrderStore((s) => s.activeOrderTrackingStatus)
  const hasActiveOrder = useOrderStore((s) => s.hasActiveOrder)

  // Applied offer state
  const appliedOfferId = useCartStore((s) => s.appliedOfferId)
  const removeAppliedOffer = useCartStore((s) => s.removeAppliedOffer)
  const availableOffers = useRewardsStore((s) => s.availableOffers)
  const appliedRewardDetails = useRewardsStore((s) => s.appliedRewardDetails)
  const merchantConfig = useRewardsStore((s) => s.merchantConfig)
  const removePendingRedemption = useRewardsStore((s) => s.removePendingRedemption)
  const appliedOffer = appliedOfferId
    ? availableOffers.find((o) => o.id === appliedOfferId)
    : null

  // Derive reward banner display text
  const rewardBannerTitle = useMemo(() => {
    if (appliedRewardDetails.length === 0 || !merchantConfig) return undefined

    const detail = appliedRewardDetails[0]
    if (!detail) return undefined

    if (merchantConfig.redemption_type === 'dollar') {
      const dollarValue = merchantConfig.dollar_value && merchantConfig.point_value
        ? (detail.pointValue / merchantConfig.point_value) * merchantConfig.dollar_value
        : detail.pointValue
      return `$${dollarValue.toFixed(2)} discount`
    }

    // Item reward — look up item name from redemption groups
    const groups = merchantConfig.item_redemption_groups ?? []
    const group = groups.find((g) => g.id === detail.groupId)
    if (group && detail.itemId) {
      const item = group.items.find((i) => i.id === detail.itemId)
      const itemName = item?.menu_item?.name
      if (itemName && itemName.length <= 20) {
        return `Free ${itemName}`
      }
      return 'Free item'
    }

    return group?.reward_name ?? 'Reward applied'
  }, [appliedRewardDetails, merchantConfig])

  // Sync: clear pending redemption when a reward cart item is removed
  // (e.g. user taps the trash icon on the free item directly)
  const cartItems = useCartStore((s) => s.items)
  const prevRewardMenuItemIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (merchantConfig?.redemption_type === 'dollar') return
    if (appliedRewardDetails.length === 0) return

    const currentRewardIds = new Set(
      cartItems.filter((i) => i.isRewardRedemption).map((i) => i.menuItem.id)
    )

    const prevIds = prevRewardMenuItemIdsRef.current
    prevRewardMenuItemIdsRef.current = currentRewardIds

    // Only act when a reward item was removed (was in prev, not in current)
    if (prevIds.size === 0) return

    const groups = merchantConfig?.item_redemption_groups ?? []
    for (const reward of appliedRewardDetails) {
      if (!reward.itemId) continue
      const group = groups.find((g) => g.id === reward.groupId)
      const rewardItem = group?.items.find((i) => i.id === reward.itemId)
      const menuItemId = rewardItem?.menu_item?.id || reward.itemId

      if (prevIds.has(menuItemId) && !currentRewardIds.has(menuItemId)) {
        removePendingRedemption(reward.groupId)
      }
    }
  }, [cartItems, appliedRewardDetails, merchantConfig, removePendingRedemption])

  // Offer warning alert hook — when the cart store auto-removes an offer
  // (e.g. required product removed, subtotal dropped), supply interactive
  // buttons matching Flutter's behaviour instead of a plain dismiss.
  const offerAlert = useOfferAlerts({
    onAutoRemoval: (warningType, offerId) => {
      switch (warningType) {
        case OfferWarningType.minimumOrderNotMet:
          return {
            // "CONTINUE" = dismiss, offer stays applied at $0 discount
            onPrimary: () => {},
            // "REMOVE OFFER" = explicitly remove (offer is no longer auto-removed)
            onSecondary: () => {
              useCartStore.getState().removeAppliedOffer()
            },
          }
        case OfferWarningType.missingRequiredItem:
          return {
            // "ADD ITEM(S) TO ORDER" → navigate to offers and auto-start the flow
            onPrimary: () => navigate('/offers', { state: { autoStartOfferId: offerId } }),
            // "REMOVE OFFER" → already removed, just dismiss
            onSecondary: () => {},
          }
        default:
          return {}
      }
    },
  })

  // Clover card-add hook
  const cloverCardAdd = useCloverCardAdd({
    merchantId,
    onSuccess: async () => {
      setIsAddCardOpen(false)
      await checkoutViewModel.refreshPaymentMethods()
    },
  })

  // Checkout sheet state
  const [isCheckoutSheetOpen, setIsCheckoutSheetOpen] = useState(false)
  // Offer detail sheet state
  const [isOfferDetailSheetOpen, setIsOfferDetailSheetOpen] = useState(false)
  // Reward removal alert state
  const [isRewardAlertVisible, setIsRewardAlertVisible] = useState(false)
  // Message shown when checkout is blocked by items requiring review.
  const [requiresReviewMessage, setRequiresReviewMessage] = useState<string | null>(null)
  // Alert shown when user tries to checkout with an active order already in progress.
  const [activeOrderAlertVisible, setActiveOrderAlertVisible] = useState(false)
  // Alert shown when location is not accepting orders (outside operating hours)
  const [closedLocationAlert, setClosedLocationAlert] = useState<{ visible: boolean; hoursText: string } | null>(null)

  // Auto-dismiss the review message when all requiresReview flags are cleared
  // (e.g. after the user edits an item and saves with complete customizations).
  const reviewBlocked = hasItemsRequiringReview()
  useEffect(() => {
    if (!reviewBlocked && requiresReviewMessage) {
      setRequiresReviewMessage(null)
    }
  }, [reviewBlocked, requiresReviewMessage])

  /**
   * Opens the checkout sheet after gating on items-requiring-review and
   * validating any applied offer.  If the offer fails validation the user
   * sees the appropriate warning dialog and checkout is blocked until
   * they resolve it.
   */
  const proceedToCheckout = useCallback(async () => {
    setRequiresReviewMessage(null)
    setIsCheckoutSheetOpen(true)
    await checkoutViewModel.refreshCheckoutData()
  }, [checkoutViewModel])

  const handleCheckout = async () => {
    // Gate: check if location is currently accepting orders
    const location = useRestaurantStore.getState().currentLocation
    if (location) {
      const isDebugMode = useRestaurantStore.getState().isDebugMode
      const locationService = new WebLocationService(null, isDebugMode)
      if (!locationService.checkLocationAvailability(location)) {
        setClosedLocationAlert({
          visible: true,
          hoursText: locationService.formatHoursText(location),
        })
        return
      }
    }

    if (hasActiveOrder()) {
      setActiveOrderAlertVisible(true)
      return
    }

    if (reviewBlocked) {
      setRequiresReviewMessage(
        'Some items need your attention before checkout. Please tap each flagged item to complete required selections.'
      )
      return
    }

    // --- Offer revalidation guard ---
    const cartState = useCartStore.getState()
    if (cartState.appliedOfferId) {
      const rewardsState = useRewardsStore.getState()
      const offer = rewardsState.availableOffers.find(
        (o) => o.id === cartState.appliedOfferId
      )

      if (offer) {
        const subtotal = cartState.items.reduce(
          (sum, item) => sum + item.totalPrice,
          0
        )
        const result = validateOffer(offer, {
          subtotal,
          cartItems: cartState.items,
          currentStoreId: cartState.restaurantId || '',
          appliedOfferId: null,
          appliedRewardId: null,
          merchantRewardConfig: rewardsState.merchantConfig || undefined,
        })

        if (result.status === 'minimum_purchase_not_met') {
          offerAlert.showWarning(
            OfferWarningType.minimumOrderNotMetForOffer,
            offer,
            {
              onPrimary: () => {
                // "CONTINUE TO CHECKOUT" — remove offer, proceed
                cartState.removeAppliedOffer()
                proceedToCheckout()
              },
              onSecondary: () => {
                // "BACK TO CART" — dismiss, stay on cart
              },
            },
            { minimumPurchase: offer.minimumPurchase }
          )
          return
        }

        if (result.status === 'outside_time_constraints') {
          offerAlert.showWarning(
            OfferWarningType.offerNotAvailableAtThisTime,
            offer,
            {
              onPrimary: () => {
                // "REMOVE OFFER" — remove offer, proceed to checkout
                cartState.removeAppliedOffer()
                proceedToCheckout()
              },
            }
          )
          return
        }

        if (result.status === 'expired' || result.status === 'upcoming') {
          offerAlert.showWarning(
            OfferWarningType.offerNoLongerValid,
            offer,
            {
              onPrimary: () => {
                // "CONTINUE TO CHECKOUT" — remove offer, proceed
                cartState.removeAppliedOffer()
                proceedToCheckout()
              },
              onSecondary: () => {
                // "BACK TO CART" — dismiss, stay on cart
              },
            }
          )
          return
        }

        if (result.status !== 'valid') {
          // Silent fallback for other statuses: remove and proceed
          cartState.removeAppliedOffer()
        }
      }
    }

    await proceedToCheckout()
  }

  const handleCheckoutSheetClose = () => {
    setIsCheckoutSheetOpen(false)
    // Reset view mode when the sheet is closed so it starts fresh next time.
    setViewMode('checkout')
    setCompletedOrder(null)
  }

  const handleIncreaseQuantity = async (itemId: string) => {
    await cartViewModel.onIncreaseQuantity(itemId)
  }

  const handleDecreaseQuantity = async (itemId: string) => {
    await cartViewModel.onDecreaseQuantity(itemId)
  }

  const handleQuantityChange = async (itemId: string, quantity: number) => {
    await cartViewModel.onQuantityChange(itemId, quantity)
  }

  // Derive sheet title from current view mode.
  const sheetTitle = viewMode === 'order-tracking' ? 'Order Status' : 'Checkout'

  // Derive sheet content based on view mode and order type.
  const renderSheetContent = () => {
    if (viewMode === 'order-tracking' && completedOrder !== null) {
      if (completedOrder.isDelivery) {
        return (
          <SheetContentDeliveryOrderStatus
            testID="sheet-content-delivery-order-status"
            {...orderTrackingViewModel.getDeliveryOrderStatusProps()}
            mapConfig={{
              customContent: currentRestaurant && completedOrder ? (
                <MapOrderTracking
                  variant="delivery"
                  restaurantLocation={{
                    lat: (getLocationForOrder(completedOrder, currentRestaurant, currentLocation ?? undefined)).latitude || 0,
                    lng: (getLocationForOrder(completedOrder, currentRestaurant, currentLocation ?? undefined)).longitude || 0,
                    name: currentRestaurant.name,
                  }}
                  deliveryAddress={
                    completedOrder.deliveryAddress
                      ? {
                          lat: completedOrder.deliveryAddress.latitude || 0,
                          lng: completedOrder.deliveryAddress.longitude || 0,
                          name: completedOrder.deliveryAddress.street,
                        }
                      : undefined
                  }
                  driverLocation={
                    activeOrderDriverLocation
                      ? { lat: activeOrderDriverLocation.latitude, lng: activeOrderDriverLocation.longitude }
                      : undefined
                  }
                  orderState={activeOrderTrackingStatus?.currentState || completedOrder.status}
                  testID="cart-tracking-delivery-map"
                />
              ) : undefined,
            }}
          />
        )
      }
      // Default to pickup tracking (covers 'pickup' and any future types)
      return (
        <SheetContentPickupOrderStatus
          testID="sheet-content-pickup-order-status"
          {...orderTrackingViewModel.getPickupOrderStatusProps()}
          mapProps={{
            showMap: true,
            children: currentRestaurant && completedOrder ? (
              <MapOrderTracking
                variant="pickup"
                restaurantLocation={{
                  lat: (getLocationForOrder(completedOrder, currentRestaurant, currentLocation ?? undefined)).latitude || 0,
                  lng: (getLocationForOrder(completedOrder, currentRestaurant, currentLocation ?? undefined)).longitude || 0,
                  name: currentRestaurant.name,
                }}
                testID="cart-tracking-pickup-map"
              />
            ) : undefined,
          }}
        />
      )
    }

    return (
      <SheetContentCheckout
        testID="checkout-sheet-content"
        showDriverTip={orderType === 'Delivery'}
        driverTipProps={checkoutViewModel.driverTipProps}
        paymentMethodProps={checkoutViewModel.paymentMethodProps}
        orderSummaryProps={checkoutViewModel.orderSummaryProps}
      />
    )
  }

  return (
    <>
      <PageContentCart
        orderType={orderType}
        address={cartViewModel.address || undefined}
        cartItems={cartViewModel.cartItems}
        subtotal={cartViewModel.subtotal}
        continueButtonText={cartViewModel.continueButtonText}
        onEditAddress={cartViewModel.onEditAddress}
        onEditCartItem={editItem.openEdit}
        onIncreaseQuantity={handleIncreaseQuantity}
        onDecreaseQuantity={handleDecreaseQuantity}
        onQuantityChange={handleQuantityChange}
        onContinue={handleCheckout}
        disabled={cartViewModel.disabled}
        loading={cartViewModel.loading}
        showOfferBanner={!!appliedOffer}
        offerTitle={appliedOffer?.title}
        offerDescription="Offer applied to this order"
        onPressOfferBanner={useCallback(() => setIsOfferDetailSheetOpen(true), [])}
        showRewardBanner={appliedRewardDetails.length > 0}
        rewardTitle={rewardBannerTitle}
        rewardDescription="Reward applied to this order"
        onPressRewardBanner={useCallback(() => setIsRewardAlertVisible(true), [])}
        testID="cart-page"
        accessibilityLabel="Shopping cart with order items and checkout options"
      />

      {/* Checkout gate message — shown when items require review before checkout */}
      {requiresReviewMessage && (
        <div
          role="alert"
          data-testid="requires-review-message"
          className="px-4 py-3 my-2 rounded-lg bg-yellow-50 border border-yellow-400 text-yellow-800 text-sm"
        >
          {requiresReviewMessage}
        </div>
      )}

      {/* Edit Cart Item Sheet */}
      <Sheet
        isOpen={editItem.editingCartItem !== null}
        onClose={editItem.cancelEdit}
        title={editItem.editingCartItem?.menuItem.name || 'Edit Item'}
        testID="edit-cart-item-sheet"
      >
        {editItem.editingCartItem && (
          <SheetContentUpdateProduct
            sectionProductInformation={{
              productName: editItem.editingCartItem.menuItem.name,
              description: editItem.editingCartItem.menuItem.description || '',
              currentPrice: editItem.formattedBasePrice,
              imageUrl: editItem.editingCartItem.menuItem.imageUrl,
            }}
            optionGroups={editOptionGroups}
            addToOrderPrice={editItem.formattedPrice}
            onAddToOrder={editItem.saveEdit}
            showSpecialRequests={false}
            specialRequests={editItem.specialInstructions}
            onSpecialRequestsChange={editItem.setSpecialInstructions}
            testID="edit-cart-item-content"
          />
        )}
      </Sheet>

      {/* Offer Detail Sheet */}
      {appliedOffer && (
        <Sheet
          isOpen={isOfferDetailSheetOpen}
          onClose={() => setIsOfferDetailSheetOpen(false)}
          title={appliedOffer.title}
          testID="offer-detail-sheet"
        >
          <SheetContentOffer
            imageProps={{
              imageUrl: appliedOffer.imageUrl,
              accessibilityLabel: `${appliedOffer.title} offer image`,
            }}
            offerDetailsProps={{
              offerTitle: appliedOffer.title,
              offerDescription: appliedOffer.description,
              buttonText: 'Remove Offer',
              buttonVariant: 'Warning',
              onOfferPress: () => {
                removeAppliedOffer()
                setIsOfferDetailSheetOpen(false)
              },
            }}
            testID="cart-offer-detail-content"
          />
        </Sheet>
      )}

      {/* Checkout / Order-Tracking Sheet */}
      <Sheet
        isOpen={isCheckoutSheetOpen}
        onClose={handleCheckoutSheetClose}
        title={sheetTitle}
        testID="checkout-sheet"
      >
        {renderSheetContent()}
      </Sheet>

      {/* Active order guard alert */}
      <Alert
        visible={activeOrderAlertVisible}
        title="Order In Progress"
        description="You already have an active order. Please wait for it to complete before placing a new one."
        primaryAction={{
          text: 'OK',
          onPress: () => setActiveOrderAlertVisible(false),
        }}
        testID="active-order-alert"
      />

      {/* Reward removal alert */}
      <Alert
        visible={isRewardAlertVisible}
        title="Remove Reward?"
        description={rewardBannerTitle ? `Remove "${rewardBannerTitle}" from this order?` : 'Remove the applied reward from this order?'}
        primaryAction={{
          text: 'Remove',
          variant: 'Warning',
          onPress: () => {
            const detail = appliedRewardDetails[0]
            if (detail) {
              // Remove the free item from the cart
              const cartState = useCartStore.getState()
              const groups = merchantConfig?.item_redemption_groups ?? []
              const group = groups.find((g) => g.id === detail.groupId)
              const rewardItem = group?.items.find((i) => i.id === detail.itemId)
              const menuItemId = rewardItem?.menu_item?.id
              if (menuItemId) {
                const cartItem = cartState.items.find(
                  (i) => i.isRewardRedemption && i.menuItem.id === menuItemId
                )
                if (cartItem) {
                  cartState.removeItem(cartItem.id)
                }
              }
              removePendingRedemption(detail.groupId)
            }
            setIsRewardAlertVisible(false)
            cartViewModel.refresh()
          },
        }}
        testID="reward-removal-alert"
      />

      {/* Offer warning alert (checkout revalidation).
          Required-prop fallbacks: alertProps is Partial<AlertProps>; when the
          hook is inactive it returns { visible: false } and Alert renders null,
          so these placeholders are never displayed. Spread wins when active. */}
      <Alert
        title=""
        description=""
        primaryAction={{ text: '', onPress: () => {} }}
        {...offerAlert.alertProps}
      />

      {/* Closed location alert — cart gate */}
      {closedLocationAlert && (
        <Alert
          title="Not Accepting Orders"
          description={closedLocationAlert.hoursText}
          primaryAction={{
            text: 'Got it',
            onPress: () => setClosedLocationAlert(null),
            variant: 'Primary',
          }}
          secondaryAction={{
            text: 'Change Location',
            onPress: () => {
              setClosedLocationAlert(null)
              useUIStore.getState().requestLocationSheet()
            },
            variant: 'Secondary',
          }}
          visible={closedLocationAlert.visible}
          testID="closed-location-alert-cart"
        />
      )}

      {/* Closed location alert — checkout gate */}
      {checkoutViewModel.closedLocationAlert && (
        <Alert
          title="Not Accepting Orders"
          description={checkoutViewModel.closedLocationAlert.hoursText}
          primaryAction={{
            text: 'Got it',
            onPress: checkoutViewModel.dismissClosedAlert,
            variant: 'Primary',
          }}
          secondaryAction={{
            text: 'Change Location',
            onPress: checkoutViewModel.onChangeLocation,
            variant: 'Secondary',
          }}
          visible={checkoutViewModel.closedLocationAlert.visible}
          testID="closed-location-alert-checkout"
        />
      )}

      {/* Clover Add Card Modal */}
      <AlertAddCardClover
        isOpen={isAddCardOpen}
        merchantId={merchantId}
        onDismiss={() => setIsAddCardOpen(false)}
        pakmsKey={cloverCardAdd.pakmsKey}
        isLoadingPakmsKey={cloverCardAdd.isLoadingPakmsKey}
        pakmsError={cloverCardAdd.pakmsError}
        isSubmitting={cloverCardAdd.isSubmitting}
        submitError={cloverCardAdd.submitError}
        onFetchPakmsKey={cloverCardAdd.fetchPakmsKey}
        onSubmitCard={cloverCardAdd.submitCard}
      />
    </>
  )
}
